import { useState } from "react";
import { useSession } from "Context/userAuthContext";
import { useNavigate } from "react-router-dom";

import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import {
  Box,
  Button,
  Typography,
  Stack,
  Toolbar,
  AppBar,
  Chip,
  Skeleton,
} from "@mui/material";
import { CreatedWith } from "Components/AllComponents";
import { shopItems, ShopCategory } from "./shopItems";

const Shop = () => {
  const { user } = useSession();
  const navigate = useNavigate();
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<"all" | "onSale" | ShopCategory>(
    "all",
  );

  const handleImageLoad = (id: number) => {
    setLoadedImages((prev) => new Set(prev).add(id));
  };

  const handleItemClick = (link: string) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handleTabChange = (newValue: "all" | "onSale" | ShopCategory) => {
    setActiveTab(newValue);
  };

  const filteredItems =
    activeTab === "all"
      ? shopItems
      : activeTab === "onSale"
        ? shopItems.filter((item) => item.onSale)
        : shopItems.filter((item) => item.category === activeTab);

  return (
    <Stack
      sx={{
        p: { xs: 2, sm: 3 },
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      <AppBar sx={{ backgroundColor: "background.default" }} elevation={0}>
        <Toolbar variant="dense">
          <Typography variant="h6" color="text.primary" flex={1}>
            Shop
          </Typography>
          <Stack direction="row" spacing={1}>
            {user ? (
              <Button
                variant="outlined"
                onClick={() => {
                  navigate("/admin/");
                }}
              >
                Admin
              </Button>
            ) : (
              <CreatedWith color="dark" />
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Stack sx={{ mt: 6 }} spacing={3}>
        <Typography>
          When you shop through our affiliate links, you pay the same price, but
          we receive a small commission that helps keep Flowlylink free.
        </Typography>

        <Box
          sx={{
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <Stack direction="row" spacing={1} sx={{ minWidth: "min-content" }}>
            <Chip
              label="All"
              onClick={() => handleTabChange("all")}
              color={activeTab === "all" ? "primary" : "default"}
              variant={activeTab === "all" ? "filled" : "outlined"}
            />
            <Chip
              label="On Sale"
              onClick={() => handleTabChange("onSale")}
              color="warning"
              variant={activeTab === "onSale" ? "filled" : "outlined"}
            />
            <Chip
              label="Cookie Cutter"
              onClick={() => handleTabChange("cookie cutter")}
              color={activeTab === "cookie cutter" ? "primary" : "default"}
              variant={activeTab === "cookie cutter" ? "filled" : "outlined"}
            />
            <Chip
              label="Bake"
              onClick={() => handleTabChange("bake")}
              color={activeTab === "bake" ? "primary" : "default"}
              variant={activeTab === "bake" ? "filled" : "outlined"}
            />
            <Chip
              label="Decorate"
              onClick={() => handleTabChange("decorate")}
              color={activeTab === "decorate" ? "primary" : "default"}
              variant={activeTab === "decorate" ? "filled" : "outlined"}
            />
            <Chip
              label="Pack"
              onClick={() => handleTabChange("pack")}
              color={activeTab === "pack" ? "primary" : "default"}
              variant={activeTab === "pack" ? "filled" : "outlined"}
            />
            <Chip
              label="Storage"
              onClick={() => handleTabChange("storage")}
              color={activeTab === "storage" ? "primary" : "default"}
              variant={activeTab === "storage" ? "filled" : "outlined"}
            />
          </Stack>
        </Box>

        <ResponsiveMasonry
          columnsCountBreakPoints={{ 639: 2, 767: 3, 991: 4, 1199: 5, 1399: 6 }}
        >
          <Masonry gutter="24px">
            {filteredItems.map((item) => (
              <Stack
                key={item.id}
                sx={{
                  p: 2,
                  backgroundColor: "white",
                  borderRadius: 4,
                  overflow: "hidden",
                  "&:hover": { cursor: "pointer", opacity: 0.85 },
                }}
                onClick={() => handleItemClick(item.link)}
              >
                <Box sx={{ position: "relative" }}>
                  {/* Skeleton loader */}
                  {!loadedImages.has(item.id) && (
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={300}
                      animation="wave"
                    />
                  )}

                  {/* Image */}
                  <img
                    src={item.imageUrl}
                    alt={`Product ${item.id}`}
                    onLoad={() => handleImageLoad(item.id)}
                    style={{
                      width: "100%",
                      height: "auto",
                      aspectRatio: "1/1",
                      objectFit: "contain",
                      display: loadedImages.has(item.id) ? "block" : "none",
                    }}
                  />

                  {/* Sale Badge */}
                  {item.onSale && (
                    <Chip
                      label="On Sale"
                      size="small"
                      color="warning"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      color: "white",
                    }}
                  >
                    {item.id}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Masonry>
        </ResponsiveMasonry>
      </Stack>
    </Stack>
  );
};

export default Shop;
